import { useEffect, useRef, useState } from 'react'
import type { Bar, CloudItem, EmojiCount, ResultsMsg } from '../lib/types'
import { Counter } from './Bits'
import { seriesOf, seriesStyle } from '../lib/series'

/** Распределение голосов: строка табло на каждый вариант. */
export function Bars({ bars, revealed }: { bars: Bar[]; revealed: boolean }) {
  const total = bars.reduce((sum, b) => sum + b.count, 0)
  // Есть ли у слайда правильный ответ вообще: в обычном опросе после раскрытия
  // гасить нечего, все варианты остаются в своих цветах.
  const scored = bars.some((b) => b.correct === true)

  return (
    // --n нужен проектору: от числа вариантов зависит кегль строк (см. .stage-body .bars).
    <div className="bars" style={{ ['--n' as string]: bars.length }}>
      {bars.map((bar, index) => {
        const share = total ? Math.round((bar.count / total) * 100) : 0
        const highlight = revealed && bar.correct === true
        const dimmed = revealed && scored && bar.correct !== true
        const series = seriesOf(index)

        return (
          <div
            key={bar.optionId}
            className={`bar${highlight ? ' bar-correct' : ''}${dimmed ? ' bar-dimmed' : ''}`}
            style={seriesStyle(index)}
          >
            {/* Буква для глаз, не для скринридера: подпись варианта идёт следом. */}
            <span className="bar-letter" aria-hidden>
              <span>{highlight ? '✓' : series.letter}</span>
            </span>
            <div>
              <div className="bar-label">
                <span>
                  {highlight && <span className="sr-only">Верный ответ: </span>}
                  {bar.text}
                </span>
                <span className="bar-share">
                  <Counter value={share} />%<span className="bar-count">{bar.count}</span>
                </span>
              </div>
              <div className="bar-track">
                <div
                  className={`bar-fill${highlight ? ' bar-fill-correct' : ''}${dimmed ? ' bar-fill-wrong' : ''}`}
                  // Ширина — ровно доля от всех голосов: рядом подписан тот же
                  // процент, и расхождение между цифрой и длиной читалось бы как ошибка.
                  style={{ width: `${share}%` }}
                />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Облако ответов целиком (а не по словам — так договорились): размер кегля
 * растёт от числа повторов, самые частые ответы видно с последнего ряда.
 */
export function WordCloud({ items, scale = 1 }: { items: CloudItem[]; scale?: number }) {
  if (items.length === 0) {
    return <p className="muted center">Пока пусто — ответы появятся здесь сразу, как их пришлют</p>
  }

  const max = Math.max(...items.map((i) => i.count))

  return (
    <div className="cloud">
      {items.map((item, index) => {
        const weight = item.count / max
        // scale > 1 — для проектора: тот же расчёт, но читаемый с дальних рядов.
        // Разброс шире прежнего: без свечения частоту ответа передаёт только кегль.
        const size = Math.round((20 + weight * 58) * scale)

        return (
          <span
            key={item.text}
            className={`cloud-item${item.pending ? ' cloud-pending' : ''}`}
            style={{ fontSize: size, color: seriesOf(index).ink }}
            title={item.count > 1 ? `${item.count} раза` : undefined}
          >
            {item.text}
            {item.count > 1 && (
              <sup style={{ fontSize: Math.max(12, size * 0.3) }}> {item.count}</sup>
            )}
          </span>
        )
      })}
    </div>
  )
}

export function EmojiTotals({ emoji }: { emoji: EmojiCount[] }) {
  if (emoji.length === 0) return null

  return (
    <div className="emoji-totals">
      {emoji.map((e) => (
        <div className="emoji-total" key={e.emoji}>
          <span>{e.emoji}</span>
          <Counter value={e.count} />
        </div>
      ))}
    </div>
  )
}

interface Fly {
  key: number
  emoji: string
  left: number
  dur: number
  drift: number
  spin: number
}

/**
 * Летящие эмодзи. Анимация полностью на CSS, React только добавляет и убирает узлы.
 *
 * Поток дельт приходит несколько раз в секунду, поэтому число одновременных
 * эмодзи ограничено: на проекторе важнее плавность, чем точность до штуки.
 */
export function EmojiFlow({
  subscribe,
}: {
  subscribe: (listener: (deltas: Record<string, number>) => void) => () => void
}) {
  const [flies, setFlies] = useState<Fly[]>([])
  const nextKey = useRef(0)

  useEffect(() => {
    return subscribe((deltas) => {
      const spawned: Fly[] = []
      for (const [emoji, count] of Object.entries(deltas)) {
        // Рисуем не больше MAX_PER_DELTA на эмодзи: при овациях счётчик улетает
        // в сотни, а экран от этого только захламляется.
        const draw = Math.min(count, MAX_PER_DELTA)
        for (let i = 0; i < draw; i++) {
          spawned.push({
            key: nextKey.current++,
            emoji,
            left: 3 + Math.random() * 94,
            dur: 2.6 + Math.random() * 1.8,
            drift: (Math.random() - 0.5) * 200,
            spin: (Math.random() - 0.5) * 100,
          })
        }
      }
      if (spawned.length === 0) return
      setFlies((current) => [...current, ...spawned].slice(-MAX_ON_SCREEN))
    })
  }, [subscribe])

  // Подчищаем улетевшие: без этого список растёт до конца эфира.
  useEffect(() => {
    const timer = window.setInterval(() => {
      setFlies((current) => (current.length > MAX_ON_SCREEN / 2 ? current.slice(-40) : current))
    }, 4000)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <div className="emoji-flow" aria-hidden>
      {flies.map((fly) => (
        <span
          key={fly.key}
          className="emoji-fly"
          style={{
            left: `${fly.left}%`,
            ['--dur' as string]: `${fly.dur}s`,
            ['--drift' as string]: `${fly.drift}px`,
            ['--spin' as string]: `${fly.spin}deg`,
          }}
        >
          {fly.emoji}
        </span>
      ))}
    </div>
  )
}

const MAX_PER_DELTA = 6
const MAX_ON_SCREEN = 120

/** Универсальный блок результатов: сам выбирает представление по типу слайда. */
export function ResultsView({
  results,
  subscribe,
  scale = 1,
}: {
  results: ResultsMsg | null
  subscribe: (listener: (deltas: Record<string, number>) => void) => () => void
  /** Множитель кегля облака: на проекторе крупнее, в пульте как есть. */
  scale?: number
}) {
  if (!results) return null

  switch (results.view) {
    case 'BARS':
      return <Bars bars={results.bars} revealed={results.revealed} />
    case 'CLOUD':
      return <WordCloud items={results.cloud} scale={scale} />
    case 'EMOJI_FLOW':
      return (
        <>
          <EmojiFlow subscribe={subscribe} />
          <EmojiTotals emoji={results.emoji} />
        </>
      )
  }
}
